/**
 * Состояние формы ручного ввода: серверный контракт ↔ строки в полях ввода.
 *
 * В состоянии всё хранится строками — так ведёт себя `<input>`, и так «пусто»
 * отличается от нуля: пустая строка означает «значения нет», а `0` — что оператор
 * ввёл ноль. Приведение к типу показателя (`dtype`) делается один раз, при
 * отправке.
 */
import type {
  Dtype,
  PassportBlock,
  PassportForm,
  SaveFormRequest,
} from '../../../requests/input_data.request';

export type Lang = 'ru' | 'kz';

/** Подпись на языке интерфейса; пустой казахский — откат на русский. */
export const pick = (ru: string, kz: string, lang: Lang): string =>
  lang === 'kz' && kz !== '' ? kz : ru;

/** Строка блока с устойчивым ключом: по индексу React путал бы строки при удалении. */
export interface BlockRow {
  uid: string;
  cells: Record<string, string>;
}

export interface BlockState {
  rows: BlockRow[];
}

export interface FormState {
  scalars: Record<string, string>;
  blocks: Record<string, BlockState>;
}

let uidCounter = 0;
const nextUid = (): string => {
  uidCounter += 1;
  return `r${String(uidCounter)}`;
};

/** Значение из ответа → строка для поля ввода. */
export const toInput = (value: unknown, dtype: Dtype): string => {
  if (value === null || value === undefined) return '';
  if (dtype === 'bool') return value === true ? 'true' : 'false';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  return '';
};

/** Строка из поля ввода → значение для отправки. Пусто → `null` («стереть»). */
export const fromInput = (raw: string, dtype: Dtype): unknown => {
  const trimmed = raw.trim();
  if (dtype === 'bool') return trimmed === 'true';
  if (trimmed === '') return null;
  if (dtype === 'str' || dtype === 'date') return trimmed;
  // Запятая как десятичный разделитель — привычный ввод в русской раскладке.
  const parsed = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(parsed)) return null;
  return dtype === 'int' ? Math.round(parsed) : parsed;
};

const rowFrom = (block: PassportBlock, raw: Record<string, unknown>): BlockRow => ({
  uid: nextUid(),
  cells: Object.fromEntries(
    block.columns.map((column) => [
      column.column,
      toInput(raw[column.column], column.dtype),
    ]),
  ),
});

/** Пустая строка блока — все объявленные колонки, чтобы форма не «прыгала». */
export const emptyRow = (block: PassportBlock): BlockRow => ({
  uid: nextUid(),
  cells: Object.fromEntries(block.columns.map((column) => [column.column, ''])),
});

export const buildState = (form: PassportForm): FormState => {
  const scalars: Record<string, string> = {};
  const blocks: Record<string, BlockState> = {};
  for (const section of form.sections) {
    for (const field of section.fields) {
      scalars[field.key] = toInput(field.value, field.dtype);
    }
    for (const block of section.blocks) {
      blocks[block.id] = { rows: block.rows.map((row) => rowFrom(block, row)) };
    }
  }
  return { scalars, blocks };
};

/** Снимок блока для сравнения «изменилось / нет» — без служебных uid. */
const blockSignature = (state: BlockState | undefined): string =>
  JSON.stringify((state?.rows ?? []).map((row) => row.cells));

export const changedScalars = (
  current: FormState,
  initial: FormState,
): string[] =>
  Object.keys(current.scalars).filter(
    (key) => current.scalars[key] !== (initial.scalars[key] ?? ''),
  );

export const changedBlocks = (current: FormState, initial: FormState): string[] =>
  Object.keys(current.blocks).filter(
    (id) => blockSignature(current.blocks[id]) !== blockSignature(initial.blocks[id]),
  );

export const dirtyCount = (current: FormState, initial: FormState): number =>
  changedScalars(current, initial).length + changedBlocks(current, initial).length;

/**
 * Поля одного акима. Сервер выбирает акима ЦЕЛОЙ строкой `general_info` — по
 * дате «в должности с», а не по году, — и в форму они приходят вместе. Значит
 * и уходить должны вместе: сохрани оператор один телефон, в строке выбранного
 * года остался бы телефон без ФИО и фото, и следующий читатель собрал бы
 * несуществующего человека — нового акима с номером прежнего.
 */
const isAkimKey = (key: string): boolean => key.startsWith('general.akim_');

/**
 * Тело запроса на сохранение: только изменённое.
 *
 * Исключение — акимский блок: правка любого его поля отправляет блок целиком
 * (см. `isAkimKey`).
 *
 * Блок уходит целиком — писатель на сервере заменяет таблицу, частичной записи
 * строк у него нет. Полностью пустые строки отбрасываем: иначе «добавил строку и
 * передумал» превращалось бы в строку-призрак в базе.
 */
export const toSaveRequest = (
  form: PassportForm,
  current: FormState,
  initial: FormState,
): SaveFormRequest => {
  const blockById = new Map<string, PassportBlock>();
  for (const section of form.sections) {
    for (const block of section.blocks) blockById.set(block.id, block);
  }

  const scalars: Record<string, unknown> = {};
  const fieldByKey = new Map<string, Dtype>();
  for (const section of form.sections) {
    for (const field of section.fields) fieldByKey.set(field.key, field.dtype);
  }
  const changed = changedScalars(current, initial);
  const keys = new Set(changed);
  if (changed.some(isAkimKey)) {
    for (const key of Object.keys(current.scalars)) {
      if (isAkimKey(key)) keys.add(key);
    }
  }
  for (const key of keys) {
    scalars[key] = fromInput(current.scalars[key], fieldByKey.get(key) ?? 'str');
  }

  const blocks: SaveFormRequest['blocks'] = [];
  for (const id of changedBlocks(current, initial)) {
    const block = blockById.get(id);
    if (block === undefined) continue;
    const rows = (current.blocks[id]?.rows ?? [])
      .filter((row) => Object.values(row.cells).some((cell) => cell.trim() !== ''))
      .map((row) =>
        Object.fromEntries(
          block.columns.map((column) => [
            column.column,
            fromInput(row.cells[column.column] ?? '', column.dtype),
          ]),
        ),
      );
    blocks.push({
      table: block.table,
      discriminator_value: block.discriminator_value,
      rows,
    });
  }

  return { kato: form.kato, year: form.year, scalars, blocks };
};

/** Заполненность раздела: сколько показателей имеют значение. */
export const sectionProgress = (
  form: PassportForm,
  state: FormState,
  index: number,
): { filled: number; total: number } => {
  const section = form.sections[index];
  let filled = 0;
  let total = 0;
  for (const field of section.fields) {
    total += 1;
    if ((state.scalars[field.key] ?? '') !== '') filled += 1;
  }
  for (const block of section.blocks) {
    // Блок считаем одним показателем: важно, заведены строки или нет.
    total += 1;
    if ((state.blocks[block.id]?.rows.length ?? 0) > 0) filled += 1;
  }
  return { filled, total };
};
